import { Input, Modal, Select, Switch } from '@lib/components'
import { FileSitemapXml } from '@lib/images'
import { Button } from '@octopus-do/octopus-storybook'
import type React from 'react'
import { useState } from 'react'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import {
  useGetDataForExport,
  useSitemapXMLExport,
} from '@/hooks/exportDropdown'
import type { ModalProps } from '@/types/modal'
import { options } from './constants'
import css from './ExportAsSitemapXml.module.scss'
import { today } from './helpers'

const ExportAsPng: React.FC<ModalProps> = ({ onClose, onBack }) => {
  const { saveSitemapXml } = useSitemapXMLExport()
  const { getExportData } = useGetDataForExport()
  const tree = getExportData().flat()

  const [rootDomain, setRootDomain] = useState(() => {
    const rootNode = tree.find((node) => node.parentId === null)
    return rootNode && rootNode.url ? rootNode.url : ''
  })
  const [customProperties, setCustomProperties] = useState(false)
  const [usePageTitleAsLoc, setUsePageTitleAsLoc] = useState(true)
  const [lastmod, setLastmod] = useState(today)
  const [changefreq, setChangefreq] = useState('always')
  const [priority, setPriority] = useState('0.5')

  const onSubmit = () => {
    sendGtmEvent(
      GtmTriggerName.EXPORT_SITEMAP_XML,
      'Clicked on generate button in Save as Sitemap.xml modal.'
    )

    const options = {
      url: rootDomain,
      lastmod,
      changefreq,
      priority,
    }

    saveSitemapXml({
      tree,
      usePageTitleAsLoc,
      options,
    })
  }

  return (
    <Modal
      title="Save as Sitemap.xml"
      onClose={onClose}
      onBack={onBack}
      width={384}
      image={<FileSitemapXml />}
      classnames={{
        content: css.content,
        body: css.body,
      }}
    >
      <div className={css.options}>
        <div className={css.input}>
          <Input
            label="Root domain"
            placeholder="https://website.com/"
            type="normal"
            id="domain"
            value={rootDomain}
            onChange={setRootDomain}
          />
        </div>
        <div className={css.option}>
          <div className={css.optionLabel}>Custom properties</div>
          <div className={css.optionSwitch}>
            <Switch
              mode="thin"
              onChange={setCustomProperties}
              checked={customProperties}
            />
          </div>
        </div>
        {customProperties && (
          <div className={css.properties}>
            <div className={css.propertiesRow}>
              <div style={{ width: 200 }}>
                <Select
                  selected={changefreq}
                  options={options}
                  onChange={(option) => setChangefreq(option as string)}
                  type="input"
                  label="Change frequency"
                  classNames={css.select}
                />
              </div>
              <div style={{ width: 88 }}>
                <Input
                  label="Priority"
                  type="normal"
                  id="priority"
                  autoComplete="off"
                  initialValue={priority}
                  onChange={setPriority}
                />
              </div>
            </div>
            <div className={css.propertiesRow}>
              <Input
                label="Last modification"
                type="normal"
                id="lastmod"
                autoComplete="off"
                placeholder="2020-09-10"
                initialValue={lastmod}
                onChange={setLastmod}
              />
            </div>
          </div>
        )}
        <div className={css.divider} />
        <div className={css.option}>
          <div
            className={css.optionLabel}
          >{`Use page titles as sitemap url <loc> element`}</div>
          <div className={css.optionSwitch}>
            <Switch
              mode="thin"
              onChange={setUsePageTitleAsLoc}
              checked={usePageTitleAsLoc}
            />
          </div>
        </div>
        <div className={css.divider} />
      </div>
      <div className={css.footer}>
        <Button label="Generate" onClick={onSubmit} />
      </div>
    </Modal>
  )
}

export default ExportAsPng
